// src/features/couleurs-metier/appliquer.ts — SCE Issues
// Application des couleurs métier (Lot 8) : surcharge des variables CSS
// `--color-metier-*` sur `:root`, au runtime.
//
// Aucun composant ne lit de hex : tous passent par `var(--color-metier-*)`
// (METIERS_COULEURS). Changer la variable suffit donc à recolorer toute
// l'application — badges, liserés, cartes, barres du tableau de bord — sans
// un nouveau rendu React.
//
// ─── Le flash de la couleur d'origine ───
//
// Les couleurs choisies sont dans SharePoint, lisibles seulement une fois
// MSAL prêt : le premier écran s'afficherait avec les couleurs d'origine,
// puis changerait. D'où une copie locale (localStorage), appliquée par
// main.tsx AVANT le premier rendu. Elle n'est qu'un cache : SharePoint fait
// foi et la remplace dès qu'il répond. Sans elle (premier passage,
// navigation privée), le flash a lieu une fois, sans autre conséquence.

import { useEffect } from 'react'
import { lireReglageCouleurs } from '@/api/couleursMetier.api'
import {
  couleursEffectives,
  lireReglage,
  variablesCss,
  type ReglageCouleurs,
} from '@/utils/couleursMetier'

const CLE_CACHE = 'sce-issues.couleurs-metier'

/** Pose les couleurs d'un réglage sur `:root`, et les garde pour le prochain démarrage. */
export function appliquerReglage(reglage: ReglageCouleurs): void {
  const racine = document.documentElement.style
  for (const [nom, valeur] of Object.entries(variablesCss(couleursEffectives(reglage)))) {
    racine.setProperty(nom, valeur)
  }
  try {
    localStorage.setItem(CLE_CACHE, JSON.stringify(reglage.metiers))
  } catch {
    // Stockage indisponible : on retombera sur le flash, rien de plus.
  }
}

/** main.tsx, avant le premier rendu : les couleurs connues au dernier passage. */
export function appliquerReglageMemorise(): void {
  try {
    const brut = localStorage.getItem(CLE_CACHE)
    if (!brut) return
    // Revalidé comme un JSON SharePoint : une teinte retirée de la palette
    // depuis ne doit pas revenir par le cache.
    appliquerReglage(lireReglage(JSON.parse(brut)))
  } catch {
    // Cache illisible : couleurs d'origine, SharePoint corrigera.
  }
}

/**
 * Lit les couleurs dans SharePoint au démarrage de la session. Monté une fois
 * dans App.tsx, sous l'authentification. Un échec est silencieux : les
 * couleurs d'origine (ou le cache) restent, et rien d'autre n'en dépend.
 */
export function ChargeurCouleursMetier(): null {
  useEffect(() => {
    let annule = false
    lireReglageCouleurs()
      .then((r) => {
        if (!annule) appliquerReglage(r)
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.warn('[couleurs-metier] lecture impossible, couleurs conservées', err)
      })
    return () => {
      annule = true
    }
  }, [])
  return null
}
